import { motion } from 'motion/react';

interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg';
  text?: string;
}

export function LoadingSpinner({ size = 'md', text }: LoadingSpinnerProps) {
  const sizeClasses = {
    sm: '16px',
    md: '24px',
    lg: '48px',
  };

  return (
    <div className="flex flex-col items-center justify-center gap-3">
      <motion.div
        style={{
          width: sizeClasses[size],
          height: sizeClasses[size],
          border: '3px solid var(--muted)',
          borderTop: '3px solid var(--primary)',
          borderRadius: '50%',
        }}
        animate={{ rotate: 360 }}
        transition={{
          duration: 1,
          repeat: Infinity,
          ease: 'linear',
        }}
      />
      {text && (
        <p style={{ color: 'var(--muted-foreground)', fontSize: 'var(--text-sm)' }}>
          {text}
        </p>
      )}
    </div>
  );
}

export function SkeletonCard() {
  return (
    <div
      style={{
        backgroundColor: 'var(--card)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-card)',
        padding: '20px',
      }}
    >
      <div className="space-y-3">
        <motion.div
          style={{
            height: '20px',
            width: '60%',
            backgroundColor: 'var(--muted)',
            borderRadius: 'var(--radius)',
          }}
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
        <motion.div
          style={{
            height: '40px',
            width: '100%',
            backgroundColor: 'var(--muted)',
            borderRadius: 'var(--radius)',
          }}
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 0.2,
          }}
        />
        <motion.div
          style={{
            height: '16px',
            width: '40%',
            backgroundColor: 'var(--muted)',
            borderRadius: 'var(--radius)',
          }}
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 0.4,
          }}
        />
      </div>
    </div>
  );
}

export function SkeletonTable({ rows = 5 }: { rows?: number }) {
  return (
    <div
      style={{
        backgroundColor: 'var(--card)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-card)',
        overflow: 'hidden',
      }}
    >
      <div style={{ padding: '20px', borderBottom: '1px solid var(--border)' }}>
        <motion.div
          style={{
            height: '24px',
            width: '30%',
            backgroundColor: 'var(--muted)',
            borderRadius: 'var(--radius)',
          }}
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
      </div>
      <div className="space-y-0">
        {Array.from({ length: rows }).map((_, i) => (
          <div
            key={i}
            style={{
              padding: '16px 20px',
              borderBottom: i < rows - 1 ? '1px solid var(--border)' : 'none',
            }}
          >
            <div className="flex items-center justify-between">
              <motion.div
                style={{
                  height: '16px',
                  width: '60%',
                  backgroundColor: 'var(--muted)',
                  borderRadius: 'var(--radius)',
                }}
                animate={{ opacity: [0.5, 1, 0.5] }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: 'easeInOut',
                  delay: i * 0.1,
                }}
              />
              <motion.div
                style={{
                  height: '16px',
                  width: '20%',
                  backgroundColor: 'var(--muted)',
                  borderRadius: 'var(--radius)',
                }}
                animate={{ opacity: [0.5, 1, 0.5] }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: 'easeInOut',
                  delay: i * 0.1 + 0.2,
                }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
